// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Stack, Badge } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { FilterBar, type FilterConfig } from '../../components/FilterBar';
import { useExceptions } from '../../hooks/useTimeAttendance';
import { formatDate } from '../../utils/format';

interface Exception {
  id: string;
  employeeId: string;
  typeId: string;
  dateStamp: string;
  severity: string;
  status: string;
  note: string | null;
}

interface ExceptionsResult {
  data: Exception[];
  total: number;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
};

const EXCEPTION_TYPE_LABELS: Record<string, string> = {
  missed_punch: 'Missed Punch',
  late_arrival: 'Late Arrival',
  early_departure: 'Early Departure',
  overtime: 'Overtime',
  absence: 'Absence',
  schedule_deviation: 'Schedule Deviation',
};

function formatExceptionType(typeId: string): string {
  return EXCEPTION_TYPE_LABELS[typeId] || typeId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ExceptionsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const { data, isLoading } = useExceptions({
    page,
    perPage: pageSize,
    startDate: filterValues.startDate || undefined,
    endDate: filterValues.endDate || undefined,
  });

  const result = data as ExceptionsResult | undefined;

  const filters: FilterConfig[] = [
    {
      key: 'severity',
      label: 'Severity',
      options: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
      clearable: true,
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'acknowledged', label: 'Acknowledged' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'dismissed', label: 'Dismissed' },
      ],
      clearable: true,
    },
  ];

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filteredData = (result?.data ?? []).filter((exc) => {
    if (filterValues.severity && exc.severity !== filterValues.severity) return false;
    if (filterValues.status && exc.status !== filterValues.status) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        exc.employeeId.toLowerCase().includes(q) ||
        formatExceptionType(exc.typeId).toLowerCase().includes(q) ||
        (exc.note?.toLowerCase().includes(q) ?? false)
      );
    }
    return true;
  });

  const columns: Column<Exception>[] = [
    {
      key: 'employeeId',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'typeId',
      label: 'Exception Type',
      render: (row) => formatExceptionType(row.typeId),
    },
    {
      key: 'dateStamp',
      label: 'Date',
      sortable: true,
      width: 140,
      render: (row) => formatDate(row.dateStamp),
    },
    {
      key: 'severity',
      label: 'Severity',
      width: 100,
      render: (row) => (
        <Badge size="sm" color={SEVERITY_COLORS[row.severity] || 'gray'} variant="light">
          {row.severity}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'note',
      label: 'Note',
      render: (row) => row.note || '-',
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Exceptions"
        subtitle="Review time and attendance exceptions requiring attention"
        breadcrumbs={[
          { label: 'Time & Attendance', to: '/admin/workforce/time' },
          { label: 'Exceptions' },
        ]}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search exceptions..."
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={filteredData as unknown as Record<string, unknown>[]}
        loading={isLoading}
        pagination={{
          page,
          pageSize,
          total: result?.total ?? 0,
          onPageChange: setPage,
        }}
        getRowKey={(row) => row.id as string}
        emptyMessage="No exceptions found for the selected filters."
      />
    </Stack>
  );
}
